export async function generateKeyPair() {
  return await window.crypto.subtle.generateKey(
    {
      name: "RSA-OAEP",
      modulusLength: 4096,
      publicExponent: new Uint8Array([1, 0, 1]),
      hash: "SHA-256",
    },
    true,
    ["encrypt", "decrypt"],
  );
}

/* ---------- Base64 helpers (ArrayBuffer <-> base64) ---------- */
export function arrayBufferToBase64(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export function base64ToArrayBuffer(base64: string) {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/* ---------- Export / Import public key (SPKI) ---------- */
export async function exportPublicKey(key: CryptoKey): Promise<string> {
  const exported = await crypto.subtle.exportKey("spki", key);
  return arrayBufferToBase64(exported);
}

export async function importPublicKey(base64Key: string): Promise<CryptoKey> {
  const binaryDer = new Uint8Array(base64ToArrayBuffer(base64Key));
  return await crypto.subtle.importKey(
    "spki",
    binaryDer,
    { name: "RSA-OAEP", hash: "SHA-256" },
    true,
    ["encrypt"],
  );
}

/* ---------- Export / Import private key (PKCS8) for backup restore ---------- */
export async function exportPrivateKeyToBase64(privateKey: CryptoKey): Promise<string> {
  const exported = await crypto.subtle.exportKey("pkcs8", privateKey);
  return arrayBufferToBase64(exported);
}

export async function importPrivateKeyFromBase64(base64Pkcs8: string): Promise<CryptoKey> {
  const raw = base64ToArrayBuffer(base64Pkcs8);
  return await crypto.subtle.importKey(
    "pkcs8",
    raw,
    { name: "RSA-OAEP", hash: "SHA-256" },
    true,
    ["decrypt"],
  );
}

/* ---------- RSA helpers ---------- */
export async function encryptWithPublicKey(publicKey: CryptoKey, data: ArrayBuffer) {
  const encrypted = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, publicKey, data);
  return arrayBufferToBase64(encrypted);
}

export async function decryptWithPrivateKey(encryptedBase64: string, privateKey: CryptoKey) {
  const binary = base64ToArrayBuffer(encryptedBase64);
  return await crypto.subtle.decrypt({ name: "RSA-OAEP" }, privateKey, binary);
}

/* ---------- AES-GCM symmetric helpers (used for message content) ---------- */
export async function encryptWithAESKey(message: string, key: CryptoKey) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(message);
  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoded);
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(encrypted), iv.length);
  return arrayBufferToBase64(combined.buffer);
}

export async function decryptWithAESKey(encryptedBase64: string, rawKeyBuffer: ArrayBuffer) {
  const combined = new Uint8Array(base64ToArrayBuffer(encryptedBase64));
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);
  const symmetricKey = await crypto.subtle.importKey(
    "raw",
    rawKeyBuffer,
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );
  const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, symmetricKey, ciphertext);
  return new TextDecoder().decode(decrypted);
}

/* ---------- Message helpers: encrypt for both parties ---------- */
export async function encryptMessageForBothParties(
  message: string,
  senderPublicKey: CryptoKey,
  recipientPublicKey: CryptoKey,
) {
  const aesKey = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const encryptedContent = await encryptWithAESKey(message, aesKey);
  const rawKey = await crypto.subtle.exportKey("raw", aesKey);
  const keyForSender = await encryptWithPublicKey(senderPublicKey, rawKey);
  const keyForRecipient = await encryptWithPublicKey(recipientPublicKey, rawKey);

  return {
    content: encryptedContent,
    keyForSender,
    keyForRecipient,
  };
}

/* ---------- Message decrypt helper ---------- */
export async function decryptMessageForBothParties(
  encryptedContent: string,
  encryptedSymmetricKey: string,
  privateKey: CryptoKey,
) {
  // decrypt AES raw key via RSA-OAEP
  const rawKeyBuffer = await decryptWithPrivateKey(encryptedSymmetricKey, privateKey);
  return await decryptWithAESKey(encryptedContent, rawKeyBuffer);
}

/* ---------- Password-derived master key (PBKDF2->AES-GCM) ---------- */
export async function deriveMasterKey(
  password: string,
  salt: Uint8Array,
  iterations = 150_000,
) {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    "raw",
    enc.encode(password),
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  return await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: new Uint8Array(salt),
      iterations,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
}

/* ---------- Backup: encrypt private key (pkcs8) with master key and return base64 strings ---------- */
export async function encryptPrivateKeyForBackup(privateKey: CryptoKey, masterKey: CryptoKey) {
  const raw = await crypto.subtle.exportKey("pkcs8", privateKey);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, masterKey, raw);
  return {
    encryptedPrivateKey: arrayBufferToBase64(encrypted),
    iv: arrayBufferToBase64(iv.buffer),
  };
}

/* ---------- Restore: decrypt encryptedPrivateKey (base64) with masterKey, return imported privateKey ---------- */
export async function decryptPrivateKeyFromBackup(encryptedBase64: string, ivBase64: string, masterKey: CryptoKey) {
  const encryptedBuffer = base64ToArrayBuffer(encryptedBase64);
  const iv = new Uint8Array(base64ToArrayBuffer(ivBase64));
  const decryptedRaw = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, masterKey, encryptedBuffer);
  // import into CryptoKey
  return await crypto.subtle.importKey("pkcs8", decryptedRaw, { name: "RSA-OAEP", hash: "SHA-256" }, true, ["decrypt"]);
}

/* ---------- Helpers to convert salt/iv to/from base64 ---------- */
export function bytesToBase64(u8: Uint8Array) {
  return arrayBufferToBase64(u8.buffer instanceof ArrayBuffer ? u8.buffer.slice(0) : new Uint8Array(u8).buffer);
}

export function base64ToBytes(b64: string) {
  return new Uint8Array(base64ToArrayBuffer(b64));
}
